/**
 * Game-setup helpers.
 *
 * This file used to contain the full legacy game state machine (playAction,
 * endRound, counterAttack, etc.) that has since been superseded by
 * playtestEngine.ts. The dead code was deleted on 2026-04-30.
 *
 * What remains here are the two setup helpers that are still the live
 * constructors for the playtest:
 *  - setupPlayer — builds an initial PlayerState from a Deck
 *  - setupGame   — builds an initial GameState from two decks
 *
 * Both are imported by playtestEngine.ts (`buildPlayerLegacy`, used inside
 * its own beginPlaytest) and by PlaytestSolo.tsx. If you add a new field to
 * PlayerState in src/types/game.ts, you MUST initialize it in setupPlayer
 * below — TypeScript will tell you if you forget. There is no other
 * construction site for a fresh player state.
 */
import type { Card } from "@/types/card";
import type { Deck } from "@/types/deck";
import type {
  GameState,
  PlayerState,
  EquipSlot,
  ZoneCard,
  SigMoveSlot,
} from "@/types/game";
import { uid } from "@/lib/utils";

export function setupPlayer(
  num: 1 | 2,
  name: string,
  deck: Deck,
  cardMap: Map<string, Card>,
): PlayerState {
  const slots: EquipSlot[] = Array.from({ length: 8 }, () => ({ cardId: null, tapped: false }));
  // Pre-fill weapon slots from deck.equipmentIds (up to 8)
  deck.equipmentIds.slice(0, 8).forEach((cid, i) => {
    slots[i] = { cardId: cid, tapped: false };
  });

  // Build deck list from mainDeck entries
  const deckCards: string[] = [];
  for (const e of deck.mainDeck) {
    for (let i = 0; i < e.count; i++) deckCards.push(e.cardId);
  }

  shuffle(deckCards);

  // Champion is referenced for hooks via player.championCardId
  void (deck.championId ? cardMap.get(deck.championId) : null);
  const armorCard = deck.armorId ? cardMap.get(deck.armorId) : null;
  const armorWeight = parseFloat(armorCard?.armorFields?.weight ?? "0") || 0;

  // Signature Moves do NOT enter the hand. They live in their own board
  // slots (rendered separately). Pre-load up to 3 into PlayerState.
  const sigMoveIds = deck.signatureMoveIds.slice(0, 3);
  const sigMoveSlots: SigMoveSlot[] = sigMoveIds.map((cid) => ({
    cardId: cid,
    used: false,
    inPlay: false,
    instanceId: uid("sig_"),
  }));

  // Starting stamina = 10 minus the armor's weight (lighter armor = more
  // stamina to spend during the match).
  const startingStamina = Math.max(0, 10 - armorWeight);

  // Opening hand is 7 cards on round 1; planning phase later refills back to
  // 5 each subsequent round (per spec).
  const initialHand = deckCards.slice(0, 7);
  const handInstances: ZoneCard[] = initialHand.map((cid) => ({
    id: uid("h_"),
    cardId: cid,
  }));

  return {
    num,
    name,
    health: 20,
    maxHealth: 20,
    stamina: startingStamina,
    maxStamina: startingStamina,
    focus: 0,
    attacksThisRound: 0,
    attacksDealtDamageThisRound: 0,
    damageDealtByAttackThisRound: [],
    hand: initialHand,
    deckCards: deckCards.slice(7),
    signatureMoveIds: sigMoveIds,
    signatureMovesUsed: [],
    sigMoveSlots,
    discard: [],
    equipSlots: slots,
    armorActive: Boolean(deck.armorId),
    armorCardId: deck.armorId ?? null,
    armorWearCounters: 0,
    armorBloodCounters: 0,
    armorWeight,
    championCardId: deck.championId ?? null,
    attackDefendZone: [],
    handInstances,
    utilityInPlay: [],
    utilityPlayed: [],
    playedCards: [],
    tokenArea: [],
    tokenAreaTapped: new Set<string>(),
    condemned: [],
    handVisible: true,
    adValueOverrides: new Map<string, number>(),
    counterActivatedIds: new Set<string>(),
    counterKeptIds: new Set<string>(),
    counterRefUtilityIds: new Set<string>(),
    consecutiveCounters: 0,
    stunned: false,
    stunnedActions: 0,
    tookDamageThisRound: false,
    wasStunnedThisRound: false,
    soulreaverUsedThisRound: false,
    igniteBladePowerPending: false,
    perryEffects: [],
    reflectQueue: [],
    pendingBuffs: [],
    totalHealthLost: 0,
    nextFocusThreshold: 3, // Spartacus pain tolerance threshold
    actionPhaseDraws: 0,
    counterGrantsActive: [],
    // Per-round trackers for counters / once-per-round hook flags.
    cardsDrawnThisRound: 0,
    onceFlagsThisRound: {},
  };
}

export function setupGame(
  p1Name: string,
  p2Name: string,
  d1: Deck,
  d2: Deck,
  cardMap: Map<string, Card>,
): GameState {
  // Resolve initial priority by champion Speed (higher goes first). On ties
  // we set coinFlipRequired and let the player resolve via UI.
  const ch1 = d1.championId ? cardMap.get(d1.championId) : null;
  const ch2 = d2.championId ? cardMap.get(d2.championId) : null;
  const sp1 = parseFloat(ch1?.championFields?.speed ?? "0") || 0;
  const sp2 = parseFloat(ch2?.championFields?.speed ?? "0") || 0;
  let priority: 1 | 2 = 1;
  let coinFlipRequired = false;
  let coinFlipWinner: 1 | 2 | undefined = undefined;
  if (sp1 > sp2) priority = 1;
  else if (sp2 > sp1) priority = 2;
  else {
    coinFlipRequired = true;
    // Random flip winner — they choose Go First / Go Second.
    coinFlipWinner = Math.random() < 0.5 ? 1 : 2;
  }

  return {
    status: "playing",
    round: 1,
    priority,
    turn: 1,
    players: {
      1: setupPlayer(1, p1Name, d1, cardMap),
      2: setupPlayer(2, p2Name, d2, cardMap),
    },
    log: [
      { round: 1, player: "system", message: `Match started: ${p1Name} vs ${p2Name}`, ts: Date.now() },
    ],
    winner: null,
    pendingPrompt: null,
    phase: coinFlipRequired ? "coin-flip" : "mulligan-p1",
    aggressor: priority,
    chainStreak: 0,
    consecutiveCounters: 0,
    coinFlipRequired,
    coinFlipWinner,
  };
}

function shuffle<T>(arr: T[]) {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
}
